import React from 'react';
import { AlertTriangle } from 'lucide-react';
import CodeBlock from '../components/CodeBlock';

function SectionHeading({ id, children }) {
  return (
    <h2
      id={id}
      className="font-heading text-2xl font-semibold mt-16 mb-4 scroll-mt-20"
      style={{ color: 'var(--text-primary)' }}
    >
      {children}
    </h2>
  );
}

function Paragraph({ children }) {
  return (
    <p className="leading-relaxed mb-4 font-body" style={{ color: 'var(--text-secondary)' }}>
      {children}
    </p>
  );
}

function EndpointHeader({ method, path }) {
  const badge = `badge-${method.toLowerCase()}`;
  return (
    <div className="flex items-center gap-3 mb-4 mt-8">
      <span className={badge}>{method}</span>
      <code className="font-mono" style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>{path}</code>
    </div>
  );
}

function ParamTable({ params }) {
  return (
    <div className="glass-card overflow-hidden my-4">
      <table className="param-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Required</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map((p) => (
            <tr key={p.name}>
              <td className="font-mono text-xs" style={{ color: 'var(--accent-primary)', opacity: 0.8 }}>{p.name}</td>
              <td className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>{p.type}</td>
              <td>
                {p.required ? (
                  <span className="text-xs font-semibold" style={{ color: 'var(--accent-warning)' }}>Yes</span>
                ) : (
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>No</span>
                )}
              </td>
              <td className="text-xs" style={{ color: 'var(--text-secondary)' }}>{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusCodes({ codes }) {
  const colorMap = {
    '200': 'var(--accent-success)', '201': 'var(--accent-success)', '204': 'var(--accent-success)',
    '400': 'var(--accent-warning)', '401': 'var(--accent-danger)', '403': 'var(--accent-danger)',
    '404': 'var(--accent-warning)', '409': 'var(--accent-warning)', '422': 'var(--accent-warning)',
    '429': 'var(--accent-warning)', '500': 'var(--accent-danger)',
  };
  return (
    <div className="glass-card overflow-hidden my-4">
      <table className="param-table">
        <thead>
          <tr><th>Status</th><th>Description</th></tr>
        </thead>
        <tbody>
          {codes.map((c) => (
            <tr key={c.code}>
              <td
                className="font-mono text-xs font-semibold"
                style={{ color: colorMap[c.code] || 'var(--text-secondary)' }}
              >
                {c.code}
              </td>
              <td className="text-xs" style={{ color: 'var(--text-secondary)' }}>{c.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function WormAPI() {
  return (
    <article>
      <div className="mb-12">
        <p
          className="text-xs font-mono uppercase tracking-widest mb-2"
          style={{ color: 'var(--accent-primary)', opacity: 0.6 }}
        >
          WORM Engine
        </p>
        <h1
          className="font-heading font-bold mb-4"
          style={{ fontSize: '32px', fontWeight: 700, color: 'var(--text-primary)' }}
        >
          Write Once, Read Many
        </h1>
        <Paragraph>
          The WORM Engine provides tamper-proof, immutable storage for evidence
          records. Once written, records cannot be modified or deleted. Each
          record is cryptographically anchored with SHA-256 and optionally
          blockchain-anchored for external verifiability.
        </Paragraph>

        {/* Immutability callout card */}
        <div
          className="glass-card p-4 my-6"
          style={{ borderLeft: '3px solid var(--accent-warning)' }}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              className="w-5 h-5 flex-shrink-0 mt-0.5"
              style={{ color: 'var(--accent-warning)' }}
            />
            <div>
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                Immutability Guarantee
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                WORM records are cryptographically chained. Any attempt to modify a record
                will break the chain and trigger an integrity alert. This is enforced at
                the storage layer and cannot be bypassed via API.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Write Once ── */}
      <SectionHeading id="write-once">Write Once</SectionHeading>
      <Paragraph>
        Create an immutable WORM record. The record is hashed, timestamped, and
        linked to the previous record in the chain. Once created, it cannot be
        modified or deleted.
      </Paragraph>

      <EndpointHeader method="POST" path="/api/v1/worm/write" />

      <ParamTable
        params={[
          { name: 'evidence_id', type: 'string', required: true, description: 'UUID of the evidence to anchor.' },
          { name: 'content_hash', type: 'string', required: true, description: 'SHA-256 hash of the evidence content.' },
          { name: 'metadata', type: 'object', required: false, description: 'Additional metadata to store with the WORM record.' },
          { name: 'anchor_blockchain', type: 'boolean', required: false, description: 'If true, also anchor to an external blockchain (default: false).' },
          { name: 'retention_years', type: 'integer', required: false, description: 'Minimum retention period in years (default: 7).' },
        ]}
      />

      <CodeBlock
        title="Request"
        examples={{
          curl: `curl -X POST https://api.noyka.ai/api/v1/worm/write \\
  -H "Authorization: Bearer eyJhbGciOi..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "evidence_id": "ev_8f3a2b1c4d5e6789",
    "content_hash": "a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a",
    "metadata": {
      "case_id": "550e8400-e29b-41d4-a716-446655440000",
      "evidence_type": "image",
      "original_filename": "photo.jpg"
    },
    "anchor_blockchain": true,
    "retention_years": 10
  }'`,
          python: `response = requests.post(
    "https://api.noyka.ai/api/v1/worm/write",
    headers={
        "Authorization": "Bearer eyJhbGciOi...",
        "Content-Type": "application/json",
    },
    json={
        "evidence_id": "ev_8f3a2b1c4d5e6789",
        "content_hash": "a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a",
        "metadata": {
            "case_id": "550e8400-e29b-41d4-a716-446655440000",
            "evidence_type": "image",
            "original_filename": "photo.jpg",
        },
        "anchor_blockchain": True,
        "retention_years": 10,
    },
)
worm_record = response.json()`,
          javascript: `const response = await fetch("https://api.noyka.ai/api/v1/worm/write", {
  method: "POST",
  headers: {
    Authorization: "Bearer eyJhbGciOi...",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    evidence_id: "ev_8f3a2b1c4d5e6789",
    content_hash: "a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a",
    metadata: {
      case_id: "550e8400-e29b-41d4-a716-446655440000",
      evidence_type: "image",
      original_filename: "photo.jpg",
    },
    anchor_blockchain: true,
    retention_years: 10,
  }),
});
const wormRecord = await response.json();`,
          go: `payload := map[string]interface{}{
    "evidence_id":  "ev_8f3a2b1c4d5e6789",
    "content_hash": "a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a",
    "metadata": map[string]string{
        "case_id":           "550e8400-e29b-41d4-a716-446655440000",
        "evidence_type":     "image",
        "original_filename": "photo.jpg",
    },
    "anchor_blockchain": true,
    "retention_years":   10,
}
body, _ := json.Marshal(payload)
req, _ := http.NewRequest("POST", "https://api.noyka.ai/api/v1/worm/write",
    bytes.NewBuffer(body))
req.Header.Set("Authorization", "Bearer eyJhbGciOi...")
req.Header.Set("Content-Type", "application/json")
resp, err := http.DefaultClient.Do(req)`,
        }}
      />

      <CodeBlock
        title="Response 201"
        examples={{
          json: `{
  "id": "worm_9e8d7c6b5a4f3210",
  "evidence_id": "ev_8f3a2b1c4d5e6789",
  "content_hash": "a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a",
  "chain_hash": "b8c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0",
  "previous_record_id": "worm_7c6b5a4f32109e8d",
  "sequence_number": 1847,
  "metadata": {
    "case_id": "550e8400-e29b-41d4-a716-446655440000",
    "evidence_type": "image",
    "original_filename": "photo.jpg"
  },
  "blockchain_anchor": {
    "status": "pending",
    "network": "ethereum",
    "estimated_confirmation": "2026-03-13T10:45:00Z"
  },
  "retention_until": "2036-03-13T10:30:00Z",
  "immutable": true,
  "created_at": "2026-03-13T10:30:01Z"
}`,
        }}
      />

      <StatusCodes
        codes={[
          { code: '201', description: 'WORM record created successfully.' },
          { code: '400', description: 'Invalid request body or hash format.' },
          { code: '401', description: 'Invalid or expired authentication token.' },
          { code: '404', description: 'Referenced evidence not found.' },
          { code: '409', description: 'WORM record already exists for this evidence hash.' },
        ]}
      />

      {/* Blockchain Anchor Details */}
      <div className="glass-card p-5 my-6">
        <h4 className="font-heading font-medium text-sm mb-3" style={{ color: 'var(--text-primary)' }}>
          Blockchain Anchoring
        </h4>
        <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
          When <code className="font-mono text-xs" style={{ color: 'var(--accent-primary)' }}>anchor_blockchain</code> is
          set to true, the WORM record hash is submitted to the Ethereum network. Confirmation
          typically takes 2-5 minutes. The blockchain transaction hash is returned in the
          verification response once confirmed.
        </p>
        <div className="font-mono text-xs space-y-1" style={{ color: 'var(--text-muted)' }}>
          <div>Network: Ethereum Mainnet</div>
          <div>Avg. Confirmation: ~3 minutes</div>
          <div>Gas: Covered by Noyka infrastructure</div>
        </div>
      </div>

      {/* ── Verify ── */}
      <SectionHeading id="verify">Verify Integrity</SectionHeading>
      <Paragraph>
        Verify the integrity of a WORM record by its content hash. The engine
        re-computes the chain hash and compares it against the stored value. If
        blockchain-anchored, external verification is also performed.
      </Paragraph>

      <EndpointHeader method="GET" path="/api/v1/worm/verify/{hash}" />

      <ParamTable
        params={[
          { name: 'hash', type: 'string', required: true, description: 'SHA-256 content hash to verify (path parameter).' },
          { name: 'deep', type: 'boolean', required: false, description: 'If true, verify the entire chain from genesis to this record (default: false).' },
          { name: 'include_blockchain', type: 'boolean', required: false, description: 'If true, include blockchain anchor verification (default: true).' },
        ]}
      />

      <CodeBlock
        title="Request"
        examples={{
          curl: `curl "https://api.noyka.ai/api/v1/worm/verify/a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a?deep=true" \\
  -H "Authorization: Bearer eyJhbGciOi..."`,
          python: `content_hash = "a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a"
response = requests.get(
    f"https://api.noyka.ai/api/v1/worm/verify/{content_hash}",
    headers={"Authorization": "Bearer eyJhbGciOi..."},
    params={"deep": True},
)
verification = response.json()`,
          javascript: `const hash = "a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a";
const response = await fetch(
  \`https://api.noyka.ai/api/v1/worm/verify/\${hash}?deep=true\`,
  { headers: { Authorization: "Bearer eyJhbGciOi..." } }
);
const verification = await response.json();`,
          go: `hash := "a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a"
url := fmt.Sprintf("https://api.noyka.ai/api/v1/worm/verify/%s?deep=true", hash)
req, _ := http.NewRequest("GET", url, nil)
req.Header.Set("Authorization", "Bearer eyJhbGciOi...")
resp, err := http.DefaultClient.Do(req)`,
        }}
      />

      <CodeBlock
        title="Response 200"
        examples={{
          json: `{
  "record_id": "worm_9e8d7c6b5a4f3210",
  "content_hash": "a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a",
  "verification": {
    "status": "verified",
    "chain_intact": true,
    "hash_match": true,
    "chain_depth": 1847,
    "verified_at": "2026-03-13T11:00:00Z"
  },
  "blockchain_verification": {
    "status": "confirmed",
    "network": "ethereum",
    "tx_hash": "0x1a2b3c4d5e6f7890abcdef1234567890abcdef1234567890abcdef1234567890",
    "block_number": 19847523,
    "confirmations": 142
  },
  "zkp_proof": {
    "type": "groth16",
    "proof_hash": "0xf1e2d3c4b5a69870...",
    "verifier_contract": "0x7890abcdef123456...",
    "verified": true
  },
  "record_metadata": {
    "created_at": "2026-03-13T10:30:01Z",
    "retention_until": "2036-03-13T10:30:00Z",
    "sequence_number": 1847
  }
}`,
        }}
      />

      <StatusCodes
        codes={[
          { code: '200', description: 'Verification completed successfully.' },
          { code: '400', description: 'Invalid hash format.' },
          { code: '401', description: 'Invalid or expired authentication token.' },
          { code: '404', description: 'No WORM record found for this hash.' },
        ]}
      />

      {/* ── Audit Log ── */}
      <SectionHeading id="audit-log">Audit Log</SectionHeading>
      <Paragraph>
        Retrieve the WORM audit log showing all write and verification operations.
        The audit log itself is immutable and forms a secondary verification chain.
      </Paragraph>

      <EndpointHeader method="GET" path="/api/v1/worm/audit" />

      <ParamTable
        params={[
          { name: 'evidence_id', type: 'string', required: false, description: 'Filter by evidence ID.' },
          { name: 'action', type: 'string', required: false, description: 'Filter by action: write, verify, chain_check, blockchain_anchor.' },
          { name: 'status', type: 'string', required: false, description: 'Filter by result status: success, failure, pending.' },
          { name: 'from', type: 'ISO 8601', required: false, description: 'Start of time range.' },
          { name: 'to', type: 'ISO 8601', required: false, description: 'End of time range.' },
          { name: 'page', type: 'integer', required: false, description: 'Page number (default: 1).' },
          { name: 'per_page', type: 'integer', required: false, description: 'Items per page, max 100 (default: 50).' },
        ]}
      />

      <CodeBlock
        title="Request"
        examples={{
          curl: `curl "https://api.noyka.ai/api/v1/worm/audit?action=verify&from=2026-03-01T00:00:00Z&per_page=20" \\
  -H "Authorization: Bearer eyJhbGciOi..."`,
          python: `response = requests.get(
    "https://api.noyka.ai/api/v1/worm/audit",
    headers={"Authorization": "Bearer eyJhbGciOi..."},
    params={
        "action": "verify",
        "from": "2026-03-01T00:00:00Z",
        "per_page": 20,
    },
)
audit_log = response.json()`,
          javascript: `const params = new URLSearchParams({
  action: "verify",
  from: "2026-03-01T00:00:00Z",
  per_page: "20",
});
const response = await fetch(
  \`https://api.noyka.ai/api/v1/worm/audit?\${params}\`,
  { headers: { Authorization: "Bearer eyJhbGciOi..." } }
);
const auditLog = await response.json();`,
          go: `req, _ := http.NewRequest("GET", "https://api.noyka.ai/api/v1/worm/audit", nil)
q := req.URL.Query()
q.Add("action", "verify")
q.Add("from", "2026-03-01T00:00:00Z")
q.Add("per_page", "20")
req.URL.RawQuery = q.Encode()
req.Header.Set("Authorization", "Bearer eyJhbGciOi...")
resp, err := http.DefaultClient.Do(req)`,
        }}
      />

      <CodeBlock
        title="Response 200"
        examples={{
          json: `{
  "data": [
    {
      "id": "audit_f1e2d3c4b5a69870",
      "action": "verify",
      "status": "success",
      "record_id": "worm_9e8d7c6b5a4f3210",
      "evidence_id": "ev_8f3a2b1c4d5e6789",
      "actor": "usr_a1b2c3d4",
      "details": {
        "verification_type": "deep",
        "chain_depth": 1847,
        "duration_ms": 234
      },
      "timestamp": "2026-03-13T11:00:00Z"
    },
    {
      "id": "audit_a9b8c7d6e5f43210",
      "action": "verify",
      "status": "success",
      "record_id": "worm_3c4d5e6f7a8b9012",
      "evidence_id": "ev_2c3d4e5f6a7b8901",
      "actor": "system",
      "details": {
        "verification_type": "scheduled",
        "chain_depth": 1846,
        "duration_ms": 189
      },
      "timestamp": "2026-03-13T06:00:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "per_page": 20,
    "total_items": 312,
    "total_pages": 16
  }
}`,
        }}
      />

      <StatusCodes
        codes={[
          { code: '200', description: 'Audit log retrieved successfully.' },
          { code: '400', description: 'Invalid query parameters.' },
          { code: '401', description: 'Invalid or expired authentication token.' },
          { code: '404', description: 'No audit records found matching filters.' },
        ]}
      />
    </article>
  );
}
